<script lang="ts" module>
	export type MediaCardVariant = 'dark' | 'light';

	export interface MediaCardProps {
		dataCy: string;
		src?: string;
		alt?: string;
		class?: string;
		loading?: boolean;
		/** Load the image eagerly at high priority, for a card that is above the fold. */
		priority?: boolean;
		/** `sizes` for the image's srcset; defaults to full width on a phone, half from md. */
		sizes?: string;
		forceHorizontalOnMobile?: boolean | undefined;
		variant?: MediaCardVariant;
		imageOverlay?: import('svelte').Snippet;
		title?: import('svelte').Snippet;
		description?: import('svelte').Snippet;
		classes?: {
			imageContainerClass?: string;
			textContainerClass?: string;
		};
	}
</script>

<script lang="ts">
	import classNames from 'classnames';
	import { twMerge } from 'tailwind-merge';
	import { CARD_IMAGE_SIZES } from '../../../constants/card-image-sizes';
	import { getDownscaledImageSrcset } from '../../../utils/getDownscaledImageSrcset/getDownscaledImageSrcset';
	import { Skeleton } from '../../Skeleton';

	let {
		dataCy,
		imageOverlay,
		title,
		description,
		forceHorizontalOnMobile = false,
		variant = 'dark',
		src = undefined,
		alt = undefined,
		classes = undefined,
		loading = false,
		priority = false,
		sizes = CARD_IMAGE_SIZES,
		...rest
	}: MediaCardProps = $props();

	let imageLoadingProps = $derived(
		priority
			? { fetchpriority: 'high' as const, loading: 'eager' as const }
			: { loading: 'lazy' as const }
	);

	let dataCyPrefix = $derived(`${dataCy}-media-card`);
</script>

<div
	class={twMerge(
		classNames('h-full w-full overflow-hidden rounded border', {
			'border-gray-800 bg-gray-900 hover:bg-gray-800': variant === 'dark',
			'transform border-gray-200 hover:shadow': variant === 'light',
			'flex sm:block': forceHorizontalOnMobile
		}),
		rest.class
	)}
>
	<div
		class={twMerge(
			'relative',
			forceHorizontalOnMobile
				? 'xs:h-[8rem] xs:min-w-[8rem] xs:max-w-[8rem] sm:min-w-none h-[6.5rem] min-w-[6.5rem] max-w-[6.5rem] sm:h-[11.25rem] sm:max-w-none'
				: 'h-[11.25rem]',
			classes?.imageContainerClass
		)}
	>
		{#if loading}
			<Skeleton class="w-ful h-full" variant="rectangular" />
		{:else}
			<img
				data-cy={`${dataCyPrefix}-image`}
				class="h-full w-full object-cover"
				{alt}
				{src}
				srcset={getDownscaledImageSrcset(src)}
				{sizes}
				decoding="async"
				{...imageLoadingProps}
			/>
			{@render imageOverlay?.()}
		{/if}
	</div>

	<div
		class={twMerge(
			classNames({
				'h-[6.5rem]': loading && forceHorizontalOnMobile,
				'pb-6': !forceHorizontalOnMobile,
				'p-4': (forceHorizontalOnMobile && !loading) || !forceHorizontalOnMobile
			}),
			classes?.textContainerClass
		)}
	>
		{@render title?.()}
		{@render description?.()}
	</div>
</div>
